import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {CustomerAuthService} from '../../../customer-auth.service';
import {Store} from '@ngrx/store';
import {
  getCurrentOrderMetaState,
  getCustomerProfile,
  getCustomerProfileUpdateStatus
} from '../../+state/stores.selectors';
import {CustomerAddress, CustomerProfile} from '../../types/CustomerSocialLogin';
import {AddOrderMeta, UpdateCustomerAddress, UpdateCustomerProfile} from '../../+state/stores.actions';
import {takeUntil, withLatestFrom} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {Utils} from '../../../../stores/utils/Utils';
import {MatDialog} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {SaveAddressDialogComponent} from './save-address-dialog/save-address-dialog.component';

@Component({
  selector: 'app-customer-save-details',
  templateUrl: './customer-save-details.component.html',
  styleUrl: './customer-save-details.component.scss'
})
export class CustomerSaveDetailsComponent implements OnInit, OnDestroy {

  unsubscribe$: Subject<void> = new Subject<void>();

  @Input() name: string;
  @Input() phone: string;
  @Input() street: string;
  @Input() floor: string;
  @Input() doorbell: string;
  @Input() zip: string;
  @Input() town: string;

  @Input() formValid = true;

  customerProfile: CustomerProfile;
  updateFired = false;
  customerProfileStatus: string;
  selectAddress: CustomerAddress;
  previousAddressesId: number[];
  savingNewAddress = false;

  constructor(
    public auth: CustomerAuthService,
    public dialog: MatDialog,
    private translateService: TranslateService,
    private store: Store<any>
  ) {
  }

  ngOnInit() {
    this.store
      .select(getCustomerProfile)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((customerProfile: CustomerProfile) => {
        this.customerProfile = customerProfile;
        if (this.savingNewAddress) {
          this.savingNewAddress = false;
          if (this.previousAddressesId && customerProfile?.addresses?.length > 0) {
            const newAddress = customerProfile.addresses.find(addr => !this.previousAddressesId.includes(addr.id));
            this.store.dispatch(new AddOrderMeta('selectedAddressId', newAddress?.id || '', {
              src: 'CustomerSaveDetailsComponent',
              description: 'Select new address after saving it',
            }));
          }
        }
      });

    this.store
      .select(getCustomerProfileUpdateStatus)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((customerProfileStatus) => {
        this.customerProfileStatus = customerProfileStatus;
      });

    this.store
      .select(getCurrentOrderMetaState)
      .pipe(
        withLatestFrom(this.store.select(getCustomerProfile)),
        takeUntil(this.unsubscribe$)
      ).subscribe(([orderMeta, customerProfile]) => {
      this.selectAddress = customerProfile?.addresses?.find(
        addr => addr.id === orderMeta?.data?.selectedAddressId
      ) || null;
    });
  }

  customerDetailsChanged(): boolean {
    let hasProfileChanges = false;
    if (this.customerProfile && this.formValid) {
      const nameChanged = this.name != null && this.customerProfile.fullName !== this.name;
      const phoneChanged = this.phone != null && this.customerProfile.phoneNumber !== this.phone;
      hasProfileChanges = nameChanged || phoneChanged;

    }
    if (hasProfileChanges && (this.customerProfileStatus === 'SUCCESS' || this.customerProfileStatus === 'FAILED')) {
      this.updateFired = false;
    }
    return hasProfileChanges;
  }

  getFirstName(): string {
    if (this.name?.trim()?.split(' ').length > 0) {
      return this.name.split(' ')[0];
    } else {
      return this.customerProfile?.firstName || '';
    }
  }

  getLastName(): string {
    if (this.name?.trim()?.split(' ').length > 1) {
      return this.name.split(' ').slice(1).join(' ');
    } else if (this.name) {
      return '';
    } else {
      return this.customerProfile?.lastName || '';
    }
  }

  updateCustomerDetails() {
    if (this.customerDetailsChanged()) {
      this.store.dispatch(
        new UpdateCustomerProfile(
          {
            firstName: this.getFirstName(),
            lastName: this.getLastName(),
            phoneNumber: this.phone,
            email: this.customerProfile.email,
          },
          {
            src: 'CustomerSaveDetailsComponent',
            description: 'Update customer profile data',
          },
        ),
      );
      this.updateFired = true;
    }
  }

  addressChanged(): boolean {
    let hasAddressChanges = false;

    function fieldChanged(formValue: string, addressValue?: string): boolean {
      return formValue != null && (addressValue || '').trim() !== (formValue || '').trim();
    }

    if (this.formValid) {
      if (this.selectAddress) {
        const streetChanged = fieldChanged(this.street, this.selectAddress.streetAddress);
        const floorChanged = fieldChanged(this.floor, this.selectAddress.floorNumber);
        const doorbellChanged = fieldChanged(this.doorbell, this.selectAddress.doorbell);
        const zipChanged = fieldChanged(this.zip, this.selectAddress.postCode);
        const townChanged = fieldChanged(this.town, this.selectAddress.city);
        hasAddressChanges = streetChanged || floorChanged || doorbellChanged || zipChanged || townChanged;
      } else {
        const streetChanged = fieldChanged(this.street);
        const floorChanged = fieldChanged(this.floor);
        const doorbellChanged = fieldChanged(this.doorbell);
        const zipChanged = fieldChanged(this.zip);
        const townChanged = fieldChanged(this.town);
        hasAddressChanges = streetChanged || floorChanged || doorbellChanged || zipChanged || townChanged;
      }
    }

    if (hasAddressChanges && (this.customerProfileStatus === 'SUCCESS' || this.customerProfileStatus === 'FAILED')) {
      this.updateFired = false;
    }

    return hasAddressChanges;
  }

  updateAddress(): void {
    if (this.addressChanged()) {
      if (this.selectAddress) {
        const message = this.translateService.instant('public.profile.address.addOrUpdateMessage', {
          address: Utils.formatAddress(this.selectAddress)
        });
        const dialogRef = this.dialog.open(SaveAddressDialogComponent, {
          width: '540px',
          data: {message},
        });

        dialogRef.afterClosed().subscribe((result) => {
          switch (result) {
            case 'update':
              this.updateSelectedAddress();
              break;

            case 'createNew':
              this.saveNewAddress();
              break;
          }
        });
      } else {
        this.saveNewAddress();
      }
    }
    this.updateFired = true;
  }

  private saveNewAddress(): void {
    const address: CustomerAddress = {
      streetAddress: this.street,
      floorNumber: this.floor,
      doorbell: this.doorbell,
      postCode: this.zip,
      city: this.town,
      isMain: false
    };
    this.savingNewAddress = true;
    this.previousAddressesId = this.customerProfile?.addresses?.map(addr => addr.id) || [];
    this.store.dispatch(
      new UpdateCustomerAddress(address, {
        src: 'CustomerSaveDetailsComponent',
        description: 'Update customer new address',
      })
    );
  }

  private updateSelectedAddress(): void {
    const address: CustomerAddress = {
      id: this.selectAddress.id,
      streetAddress: this.street,
      floorNumber: this.floor,
      doorbell: this.doorbell,
      postCode: this.zip,
      city: this.town,
      isMain: this.selectAddress.isMain
    };
    this.store.dispatch(
      new UpdateCustomerAddress(address, {
        src: 'CustomerSaveDetailsComponent',
        description: 'Update customer update address',
      })
    );
  }

  hasChanges(): boolean {
    return this.customerDetailsChanged() || this.addressChanged();
  }

  submitChanges(): void {
    if (this.hasChanges()) {
      this.updateCustomerDetails();
      this.updateAddress();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
